/**
 * FSP Mandates page — v31-1-10
 *
 * Route: /org/fsp-profile/mandates
 *
 * Features:
 * - List investment mandates from the FSP profile
 * - Add / edit / delete mandates
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import type { FspProfile, FspMandate, MandateCreateRequest } from '../../types/fspProfile';

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

const mandateApi = {
  getProfile: () => api.request<FspProfile>('/api/v1/orgs/fsp-profile'),
  createMandate: (body: MandateCreateRequest) =>
    api.request<FspMandate>('/api/v1/orgs/fsp-profile/mandates', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateMandate: (id: number, body: Partial<MandateCreateRequest>) =>
    api.request<FspMandate>(`/api/v1/orgs/fsp-profile/mandates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  deleteMandate: (id: number) =>
    api.request<void>(`/api/v1/orgs/fsp-profile/mandates/${id}`, {
      method: 'DELETE',
    }),
};

const RISK_PROFILES = ['conservative', 'moderate', 'balanced', 'growth', 'aggressive'] as const;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const FspMandates: React.FC = () => {
  const qc = useQueryClient();
  const { data: profile, isLoading, error } = useQuery<FspProfile>({
    queryKey: ['fsp-profile'],
    queryFn: mandateApi.getProfile,
    retry: false,
  });

  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [riskProfile, setRiskProfile] = useState('balanced');
  const [benchmark, setBenchmark] = useState('');

  const createMut = useMutation({
    mutationFn: mandateApi.createMandate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fsp-profile'] });
      resetForm();
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<MandateCreateRequest> }) =>
      mandateApi.updateMandate(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fsp-profile'] });
      resetForm();
    },
  });

  const deleteMut = useMutation({
    mutationFn: mandateApi.deleteMandate,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fsp-profile'] }),
  });

  const resetForm = () => {
    setShowAdd(false);
    setEditId(null);
    setName('');
    setRiskProfile('balanced');
    setBenchmark('');
  };

  const startEdit = (m: FspMandate) => {
    setEditId(m.id);
    setName(m.name);
    setRiskProfile(m.risk_profile);
    setBenchmark(m.benchmark ?? '');
    setShowAdd(false);
  };

  const handleSubmit = () => {
    const body: MandateCreateRequest = {
      name,
      risk_profile: riskProfile,
      benchmark: benchmark || undefined,
    };

    if (editId) {
      updateMut.mutate({ id: editId, body });
    } else {
      createMut.mutate(body);
    }
  };

  const mandates = profile?.mandates ?? [];
  const saving = createMut.isPending || updateMut.isPending;
  const mutError = createMut.error || updateMut.error;

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
          <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Investment Mandates</h1>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-yellow-700 dark:text-yellow-300 text-sm">
            Create an FSP profile first before adding mandates.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Investment Mandates</h1>
        {!showAdd && !editId && (
          <button
            onClick={() => setShowAdd(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            Add Mandate
          </button>
        )}
      </div>

      {/* Add / Edit form */}
      {(showAdd || editId) && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {editId ? 'Edit Mandate' : 'New Mandate'}
          </h2>
          {mutError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2">
              <p className="text-red-600 dark:text-red-400 text-sm">{(mutError as Error).message}</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="e.g. Growth Portfolio Mandate"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Risk Profile</label>
              <select
                value={riskProfile}
                onChange={(e) => setRiskProfile(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {RISK_PROFILES.map((rp) => (
                  <option key={rp} value={rp}>
                    {rp.charAt(0).toUpperCase() + rp.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Benchmark</label>
              <input
                type="text"
                value={benchmark}
                onChange={(e) => setBenchmark(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="e.g. JSE All Share Index"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={saving || !name || !riskProfile}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              {saving ? 'Saving...' : editId ? 'Update' : 'Add'}
            </button>
            <button
              onClick={resetForm}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Mandates table */}
      {mandates.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center text-gray-500 dark:text-gray-400">
          No mandates added yet.
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Risk Profile</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Benchmark</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {mandates.map((m) => (
                <tr key={m.id}>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{m.name}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded text-xs font-medium capitalize">
                      {m.risk_profile}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    {m.benchmark || '-'}
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button
                      onClick={() => startEdit(m)}
                      className="text-blue-600 dark:text-blue-400 hover:underline text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        if (confirm('Delete this mandate?')) deleteMut.mutate(m.id);
                      }}
                      disabled={deleteMut.isPending}
                      className="text-red-600 dark:text-red-400 hover:underline text-sm"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FspMandates;
